// Zod schemas for every API request and response body (design 4.2).
import { z } from 'zod';
import { ALLOWED_CONTENT_TYPES, AUDIT_ACTIONS, FIELD_TYPES, MAX_PACKAGE_SAMPLES, MAX_UPLOAD_BYTES, ROLES, } from './types.js';
import { BLUEPRINT_NAME_PATTERN, FIELD_NAME_PATTERN, MAX_DESCRIPTION, MAX_DOCUMENT_TYPE } from './schema/validate.js';
import { coverageFloorSchema, DEFAULT_COVERAGE_FLOOR } from './sets/coverage.js';
import { MAX_SET_MEMBERS_LIMIT } from './sets/membership.js';
import { MAX_CHECKS, MAX_HEADER_FIELDS, MAX_ROLLUPS } from './sets/summary.js';
export const roleSchema = z.enum(ROLES);
const isoDate = z.iso.datetime({ offset: true });
const dateOnly = z.iso.date();
export const versionRefSchema = z.union([z.number().int().positive(), z.literal('DRAFT')]);
// ---------- Blueprint schema and form ----------
export const bdaSchemaPropertySchema = z.lazy(() => z.looseObject({
    type: z.enum(['string', 'number', 'integer', 'boolean', 'array', 'object']).optional(),
    inferenceType: z.enum(['explicit', 'inferred']).optional(),
    instruction: z.string().optional(),
    description: z.string().optional(),
    items: bdaSchemaPropertySchema.optional(),
    $ref: z.string().optional(),
    properties: z.record(z.string(), bdaSchemaPropertySchema).optional(),
}));
export const bdaSchemaSchema = z.looseObject({
    $schema: z.string(),
    description: z.string(),
    class: z.string(),
    type: z.literal('object'),
    definitions: z.record(z.string(), bdaSchemaPropertySchema),
    properties: z.record(z.string(), bdaSchemaPropertySchema),
});
const fieldName = z.string().regex(FIELD_NAME_PATTERN);
export const scalarFieldSchema = z.object({
    kind: z.literal('field'),
    name: fieldName,
    type: z.enum(FIELD_TYPES),
    instruction: z.string().min(1).max(2000),
    inferenceType: z.enum(['explicit', 'inferred']),
    // Detailed rules (count, length, characters, text types only) are in validateFields.
    options: z.array(z.string().max(500)).max(100).optional(),
});
export const groupFieldSchema = z.object({
    kind: z.literal('group'),
    name: fieldName,
    description: z.string().max(2000),
    fields: z.array(scalarFieldSchema).min(1),
});
export const tableFieldSchema = z.object({
    kind: z.literal('table'),
    name: fieldName,
    instruction: z.string().min(1).max(2000),
    columns: z.array(scalarFieldSchema).min(1),
});
export const fieldNodeSchema = z.discriminatedUnion('kind', [scalarFieldSchema, groupFieldSchema, tableFieldSchema]);
const blueprintMeta = {
    name: z.string().regex(BLUEPRINT_NAME_PATTERN),
    documentType: z.string().min(1).max(64),
    description: z.string().max(MAX_DESCRIPTION),
};
export const createBlueprintRequest = z.object({ ...blueprintMeta, schema: bdaSchemaSchema });
export const updateBlueprintRequest = z.object({
    ...blueprintMeta,
    schema: bdaSchemaSchema,
    expectedRevision: z.number().int().nonnegative(),
});
export const publishRequest = z.object({ changeNote: z.string().max(500).default('') });
export const blueprintSchema = z.object({
    id: z.string(),
    name: z.string(),
    documentType: z.string(),
    description: z.string(),
    ownerSub: z.string(),
    ownerEmail: z.string(),
    bdaArn: z.string().nullable(),
    workingSchema: bdaSchemaSchema,
    revision: z.number().int(),
    latestVersion: z.number().int().nullable(),
    archived: z.boolean(),
    runCount: z.number().int(),
    copiedFrom: z.object({ blueprintId: z.string(), ownerSub: z.string(), ownerEmail: z.string() }).nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
export const blueprintSummarySchema = blueprintSchema.omit({ workingSchema: true });
export const blueprintVersionSchema = z.object({
    blueprintId: z.string(),
    version: z.number().int().positive(),
    schema: bdaSchemaSchema,
    publishedAt: isoDate,
    publishedBy: z.string(),
    changeNote: z.string(),
});
export const blueprintVersionSummarySchema = blueprintVersionSchema.omit({ schema: true });
export const adminNoticeSchema = z.object({
    blueprintId: z.string(),
    adminSub: z.string(),
    adminName: z.string(),
    action: z.enum(['DUPLICATED', 'PACKAGED']),
    at: isoDate,
});
export const blueprintPackageSummarySchema = z.object({
    id: z.string(),
    title: z.string(),
    version: z.number().int(),
    authorEmail: z.string(),
    status: z.enum(['BUILDING', 'READY', 'FAILED']),
    createdAt: isoDate,
    recipients: z.array(z.lazy(() => recipientSchema)),
});
/**
 * A Blueprint_Set of the blueprint's owner that has the blueprint as a Member (blueprint-sets Req 1.7), with the
 * Member's ref and, when another Member of that set has the same Document_Type at its ref, the conflict (Req 2.7).
 */
export const blueprintSetMembershipSchema = z.object({
    setId: z.string(),
    setName: z.string(),
    ref: versionRefSchema,
    conflict: z.object({ otherBlueprintName: z.string(), documentType: z.string() }).nullable(),
});
export const blueprintDetailResponse = z.object({
    blueprint: blueprintSchema,
    versions: z.array(blueprintVersionSummarySchema),
    notices: z.array(adminNoticeSchema),
    packages: z.array(blueprintPackageSummarySchema),
    /** blueprint-sets Req 1.7, 2.7; optional so responses without it (older APIs, fixtures) still parse (Req 13.4). */
    sets: z.array(blueprintSetMembershipSchema).optional(),
});
export const paged = (item) => z.object({ items: z.array(item), nextCursor: z.string().nullable() });
export const blueprintListResponse = paged(blueprintSummarySchema);
export const suggestRequest = z.object({ runId: z.string() });
/**
 * `truncated`: the document text was longer than the part sent to the model (design 4.3), so suggestions are based
 * on the beginning of the document only.
 */
export const suggestResponse = z.object({
    fields: z.array(fieldNodeSchema),
    truncated: z.boolean().default(false),
});
/** Most characters in the optional "What do you want to extract?" prompt (requirement 3.7.2). */
export const MAX_SUGGEST_PROMPT_CHARS = 2000;
// ---------- Blueprint sets: members, Summary_Rules, run snapshot (blueprint-sets design "Data Models") ----------
export const setMemberRefSchema = z.object({ blueprintId: z.string().min(1).max(64), ref: versionRefSchema });
const ruleId = z.string().min(1).max(64);
const memberPath = z.string().min(1).max(200);
const summarySourceSchema = z.object({ memberId: z.string().min(1).max(64), path: memberPath });
export const headerFieldRuleSchema = z.object({ id: ruleId, label: z.string().max(128), source: summarySourceSchema });
export const rollupRuleSchema = z.object({
    id: ruleId,
    name: z.string().max(128),
    aggregate: z.enum(['sum', 'count', 'min', 'max']),
    sources: z
        .array(z.union([summarySourceSchema, z.object({ memberId: z.string().min(1).max(64), segments: z.literal(true) })]))
        .max(100),
    groupBy: z
        .discriminatedUnion('kind', [
        z.object({ kind: z.literal('member') }),
        z.object({ kind: z.literal('field'), path: memberPath }),
    ])
        .optional(),
});
const operandSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('header'), id: ruleId }),
    z.object({ kind: z.literal('rollup'), id: ruleId }),
    z.object({ kind: z.literal('rollupGroup'), id: ruleId, group: z.string().max(500) }),
]);
export const checkRuleSchema = z.object({
    id: ruleId,
    name: z.string().max(128),
    left: operandSchema,
    right: operandSchema,
    op: z.enum(['eq', 'lte', 'gte']),
    tolerance: z
        .object({ kind: z.enum(['absolute', 'percent']), value: z.number().nonnegative() })
        .default({ kind: 'absolute', value: 0 }),
});
/** Shape and limits only; references and types are checked by `validateSummaryRules` against the Members. */
export const summaryRulesSchema = z.object({
    headerFields: z.array(headerFieldRuleSchema).max(MAX_HEADER_FIELDS),
    rollups: z.array(rollupRuleSchema).max(MAX_ROLLUPS),
    checks: z.array(checkRuleSchema).max(MAX_CHECKS),
});
/** What a set or set-preview run used (`outputs/<sub>/<runId>/set-snapshot.json`; design "Segment to Member"). */
export const setRunSnapshotSchema = z.object({
    setId: z.string().nullable(),
    setName: z.string(),
    setVersion: versionRefSchema.nullable(),
    members: z.array(z.object({
        index: z.number().int().nonnegative(),
        blueprintId: z.string().nullable(),
        ref: versionRefSchema.nullable(),
        name: z.string(),
        documentType: z.string(),
        bdaArn: z.string(),
        bdaName: z.string(),
        schema: bdaSchemaSchema,
    })),
    summaryRules: summaryRulesSchema,
    /** Req 2.8: snapshots written before wrong-blueprint detection parse as the default. */
    coverageFloor: coverageFloorSchema.default(DEFAULT_COVERAGE_FLOOR),
});
// ---------- Runs ----------
export const runStatusSchema = z.enum(['UPLOADING', 'QUEUED', 'PROCESSING', 'SUCCEEDED', 'FAILED']);
export const runModeSchema = z.enum(['custom', 'suggest', 'preview', 'set', 'setPreview', 'packetSuggest']);
const promptField = z
    .string()
    .trim()
    .max(MAX_SUGGEST_PROMPT_CHARS, `Keep the prompt to ${MAX_SUGGEST_PROMPT_CHARS.toLocaleString('en-US')} characters.`);
export const createRunRequest = z
    .object({
    /** `set`: a Set_Run (needs `setId` and `setVersion`); `packetSuggest`: a Packet_Suggest_Run (blueprint-sets). */
    mode: z.enum(['custom', 'suggest', 'set', 'packetSuggest']),
    blueprintId: z.string().optional(),
    version: versionRefSchema.optional(),
    setId: z.string().min(1).max(64).optional(),
    setVersion: versionRefSchema.optional(),
    fileName: z.string().min(1).max(255),
    contentType: z.enum(ALLOWED_CONTENT_TYPES),
    size: z.number().int().positive().max(MAX_UPLOAD_BYTES),
    /** Suggest and packet-suggest runs only: what the user wants extracted (requirement 3.7.2). Blank means none. */
    prompt: promptField.optional(),
})
    .refine((r) => r.mode !== 'custom' || (r.blueprintId !== undefined && r.version !== undefined), {
    message: 'blueprintId and version are required for custom runs',
    path: ['blueprintId'],
})
    .refine((r) => r.mode !== 'set' || (r.setId !== undefined && r.setVersion !== undefined), {
    message: 'setId and setVersion are required for set runs',
    path: ['setId'],
})
    .refine((r) => r.mode === 'suggest' || r.mode === 'packetSuggest' || r.prompt === undefined, {
    message: 'A prompt is only allowed for field suggestions.',
    path: ['prompt'],
});
export const runSchema = z.object({
    id: z.string(),
    ownerSub: z.string(),
    blueprintId: z.string().nullable(),
    blueprintName: z.string(),
    version: versionRefSchema.nullable(),
    mode: runModeSchema,
    fileName: z.string(),
    contentType: z.string(),
    size: z.number().int(),
    status: runStatusSchema,
    failureReason: z.string().nullable(),
    pages: z.number().int().nullable(),
    sourcePages: z.number().int().nullable().default(null),
    startedAt: isoDate,
    finishedAt: isoDate.nullable(),
    durationMs: z.number().int().nullable(),
    expired: z.boolean(),
    setId: z.string().nullable().default(null),
    setName: z.string().nullable().default(null),
    setVersion: versionRefSchema.nullable().default(null),
    /** X-Ray root trace ID of the request that created the run (only on runs created with tracing). */
    createdTraceId: z.string().optional(),
    /** X-Ray root trace ID of the invocation that wrote the run's final status (RunCompletion or a status poll). */
    completedTraceId: z.string().optional(),
});
/** Starts a Preview_Run on the document stored for `sourceRunId` (create-doc-interactive Req 1.3, 1.4, 6.1). */
export const createPreviewRequest = z.object({
    /** The Suggest_Run, or the run that holds the Editor's test document (Req 6.1). */
    sourceRunId: z.string().min(1).max(64),
    schema: bdaSchemaSchema,
});
/** Body of `POST /runs/:id/retry`; every field is optional, so an empty body repeats the run as it was. */
export const retryRunRequest = z.object({
    version: versionRefSchema.optional(),
    /** With version: run a suggest or preview run's document against this owned blueprint (Req 4.8). */
    blueprintId: z.string().optional(),
    /** With setVersion: run a set or packet-suggest run's document against this owned set (blueprint-sets Req 10.8). */
    setId: z.string().min(1).max(64).optional(),
    setVersion: versionRefSchema.optional(),
    /** Suggest runs only: replaces the stored prompt; blank after trimming means none (Req 4.3). */
    prompt: promptField.optional(),
});
export const createRunResponse = z.object({
    run: runSchema,
    upload: z.object({ url: z.url(), fields: z.record(z.string(), z.string()) }),
});
export const runListResponse = paged(runSchema);
const fieldValue = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.array(z.number()),
    z.array(z.boolean()),
    z.null(),
]);
/** A sanitized Field_Location (enlarge-preview-annotate Req 5.1, 5.9); the API writes only clamped, valid boxes. */
export const fieldLocationSchema = z.object({
    page: z.number().int().min(1),
    top: z.number().min(0).max(1),
    left: z.number().min(0).max(1),
    width: z.number().gt(0).max(1),
    height: z.number().gt(0).max(1),
});
const normalizedFields = z.array(z.object({
    path: z.string(),
    value: fieldValue,
    confidence: z.number().nullable(),
    locations: z.array(fieldLocationSchema).optional(),
    itemLocations: z.array(z.array(fieldLocationSchema)).optional(),
}));
const normalizedTables = z.array(z.object({
    name: z.string(),
    columns: z.array(z.string()),
    rows: z.array(z.record(z.string(), z.object({
        value: fieldValue,
        confidence: z.number().nullable(),
        locations: z.array(fieldLocationSchema).optional(),
    }))),
}));
export const normalizedSegmentSchema = z.object({
    index: z.number().int().nonnegative(),
    pageStart: z.number().int().positive(),
    pageEnd: z.number().int().positive(),
    status: z.string(),
    fields: normalizedFields,
    tables: normalizedTables,
    matchedMember: z.number().int().nonnegative().nullable().optional(),
    matchedBlueprint: z
        .object({ arn: z.string(), name: z.string().nullable(), confidence: z.number().nullable() })
        .nullable()
        .optional(),
    fieldCoverage: z
        .object({ found: z.number().int().nonnegative(), total: z.number().int().nonnegative() })
        .nullable()
        .optional(),
});
export const normalizedResultSchema = z.object({
    runId: z.string(),
    blueprintId: z.string().nullable(),
    version: versionRefSchema.nullable(),
    pages: z.number().int(),
    fields: normalizedFields,
    tables: normalizedTables,
    segments: z.array(normalizedSegmentSchema).optional(),
});
export const runResultResponse = z.union([
    z.object({
        expired: z.literal(false),
        result: normalizedResultSchema,
        documentUrl: z.url(),
        /** Preview runs only: the stored Preview_Schema the result was extracted with (Req 6.4). */
        schema: bdaSchemaSchema.optional(),
        /** Set and set-preview runs only: the snapshot of the set the run used (blueprint-sets Req 5, 6, 7). */
        set: setRunSnapshotSchema.optional(),
    }),
    z.object({ expired: z.literal(true) }),
]);
// ---------- Blueprint sets (blueprint-sets design "Routes", "Data Models") ----------
/** GET /runs/:id/segments/:index/text returns at most this many characters (Req 5.4, 5.10). */
export const SEGMENT_TEXT_MAX_CHARS = 200_000;
/** GET /runs/:id/segments/:index/text: one Segment's standard text; `truncated` when it was cut at the limit. */
export const segmentTextResponse = z.object({ text: z.string(), truncated: z.boolean() });
/** POST /runs/:id/segments/:index/sample on a document that is not a PDF (Req 5.4; design "Error handling"). */
export const PDF_PAGES_ONLY_MESSAGE = 'Only PDF pages can be extracted. Use the whole document instead.';
const emptyRules = () => ({ headerFields: [], rollups: [], checks: [] });
export const createSetRequest = z.object({
    name: z.string().regex(BLUEPRINT_NAME_PATTERN),
    description: z.string().max(MAX_DESCRIPTION).default(''),
    /** `maxSetMembers` is enforced by `validateSetDefinition`; 40 is BDA's per-project limit. */
    members: z.array(setMemberRefSchema).min(1).max(MAX_SET_MEMBERS_LIMIT),
    summaryRules: summaryRulesSchema.default(emptyRules),
    /** Req 2.8: a share 0..1 (outside -> 400); 0 turns wrong-blueprint detection off. */
    coverageFloor: coverageFloorSchema.default(DEFAULT_COVERAGE_FLOOR),
});
export const updateSetRequest = createSetRequest.extend({ expectedRevision: z.number().int().nonnegative() });
export const publishSetRequest = z.object({ changeNote: z.string().max(500).default('') });
const proposalMemberSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('proposed'),
        key: z.string().min(1).max(16),
        name: z.string(),
        documentType: z.string().max(MAX_DOCUMENT_TYPE),
        description: z.string().max(MAX_DESCRIPTION),
        fields: z.array(fieldNodeSchema).max(100),
    }),
    z.object({
        kind: z.literal('existing'),
        key: z.string().min(1).max(16),
        blueprintId: z.string().min(1).max(64),
        ref: versionRefSchema,
    }),
]);
/** Starts a Set_Preview_Run on the Sample_Packet stored for `sourceRunId` (Req 11). */
export const createSetPreviewRequest = z.object({
    sourceRunId: z.string().min(1).max(64),
    members: z.array(proposalMemberSchema).min(1).max(MAX_SET_MEMBERS_LIMIT),
});
/** Creates draft blueprints and a Set_Draft from a reviewed Set_Proposal (Req 10.6, 10.7). */
export const createSetFromProposalRequest = z.object({
    name: z.string().regex(BLUEPRINT_NAME_PATTERN),
    description: z.string().max(MAX_DESCRIPTION).default(''),
    types: createSetPreviewRequest.shape.members,
});
export const setSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    ownerSub: z.string(),
    ownerEmail: z.string(),
    members: z.array(setMemberRefSchema),
    summaryRules: summaryRulesSchema,
    /** Req 2.8, 13.3: items written before wrong-blueprint detection read as the default. */
    coverageFloor: coverageFloorSchema.default(DEFAULT_COVERAGE_FLOOR),
    revision: z.number().int(),
    latestVersion: z.number().int().nullable(),
    archived: z.boolean(),
    runCount: z.number().int(),
    testRunId: z.string().nullable(),
    copiedFrom: z.string().nullable().default(null),
    createdAt: isoDate,
    updatedAt: isoDate,
});
/** List row: no Summary_Rules, with the Member count (Req 1.1). */
export const setSummarySchema = setSchema.omit({ summaryRules: true }).extend({ memberCount: z.number().int() });
export const setListResponse = paged(setSummarySchema);
export const setVersionSchema = z.object({
    setId: z.string(),
    version: z.number().int().positive(),
    members: z.array(z.object({ blueprintId: z.string(), ref: z.number().int().positive() })),
    memberSnapshot: z.array(z.object({ blueprintId: z.string(), name: z.string(), documentType: z.string(), description: z.string() })),
    summaryRules: summaryRulesSchema,
    coverageFloor: coverageFloorSchema.default(DEFAULT_COVERAGE_FLOOR),
    publishedAt: isoDate,
    publishedBy: z.string(),
    changeNote: z.string(),
});
export const setVersionSummarySchema = setVersionSchema
    .omit({ members: true, memberSnapshot: true, summaryRules: true })
    .extend({
    memberCount: z.number().int(),
});
export const setVersionResponse = z.object({ version: setVersionSchema });
/** A Member of the Set_Draft as the set page shows it (Req 1.4, 2.5, 2.7, 3.3, 3.4). */
export const resolvedSetMemberSchema = z.object({
    blueprintId: z.string(),
    ref: versionRefSchema,
    exists: z.boolean(),
    name: z.string(),
    documentType: z.string(),
    description: z.string(),
    archived: z.boolean(),
    /** The blueprint's latest published version when it is newer than a numbered ref, else null (Req 3.3). */
    newerVersion: z.number().int().nullable(),
    /** BDA field paths of the Member's schema at its ref, for the Summary_Rules editor. */
    schema: bdaSchemaSchema.nullable(),
});
export const setWarningSchema = z.object({
    members: z.array(z.number().int().nonnegative()),
    kind: z.enum(['SHORT_DESCRIPTION', 'SAME_DESCRIPTION', 'SAME_SLUG']),
    message: z.string(),
});
export const setDetailResponse = z.object({
    set: setSchema,
    members: z.array(resolvedSetMemberSchema),
    /** Blocking problems of the Set_Draft as it is now (`validateSetDefinition` + `validateSummaryRules` keys). */
    errors: z.record(z.string(), z.string()),
    warnings: z.array(setWarningSchema),
    /** Document_Type conflicts between Members (Req 2.7), also present in `errors`. */
    conflicts: z.array(z.object({ members: z.array(z.number().int().nonnegative()), documentType: z.string(), message: z.string() })),
    versions: z.array(setVersionSummarySchema),
    runs: z.array(runSchema),
});
export const proposedTypeSchema = z.object({
    key: z.string(),
    name: z.string(),
    documentType: z.string(),
    description: z.string(),
    pages: z.array(z.number().int().positive()),
    fields: z.array(fieldNodeSchema),
    fieldsFrom: z.array(z.number().int().positive()),
    droppedFields: z.number().int().nonnegative(),
    error: z.string().optional(),
});
export const setProposalSchema = z.object({
    runId: z.string(),
    pages: z.number().int().nonnegative(),
    types: z.array(proposedTypeSchema),
    unassigned: z.array(z.object({ page: z.number().int().positive(), reason: z.string() })),
    unclassified: z.array(z.object({ page: z.number().int().positive(), reason: z.string() })),
    mergeFlags: z.array(z.object({ a: z.string(), b: z.string(), reason: z.string(), source: z.enum(['SAME_TYPE', 'MODEL']) })),
});
export const proposalStatusSchema = z.enum(['PENDING', 'CLASSIFYING', 'PROPOSING', 'READY', 'FAILED']);
/** `GET /set-proposals/:runId` (Req 8, 9.6, 10.1). `result` and `existingMatches` only when READY. */
export const setProposalStatusResponse = z.object({
    run: runSchema,
    proposal: z.object({
        status: proposalStatusSchema.nullable(),
        stage: z.string().nullable(),
        done: z.number().int().nullable(),
        total: z.number().int().nullable(),
        updatedAt: isoDate.nullable(),
        failure: z.string().nullable(),
        /** Model calls and token sums so far (Req 9.6); null before the first call is recorded. */
        modelCalls: z.number().int().nullable().optional(),
        inputTokens: z.number().int().nullable().optional(),
        outputTokens: z.number().int().nullable().optional(),
    }),
    result: setProposalSchema.optional(),
    /** Owned blueprints whose document type equals a proposed type's (Req 10.1). */
    existingMatches: z
        .array(z.object({
        key: z.string(),
        blueprintId: z.string(),
        name: z.string(),
        documentType: z.string(),
        latestVersion: z.number().int().nullable(),
    }))
        .optional(),
    documentUrl: z.url().nullable().optional(),
});
export const createSetFromProposalResponse = z.object({ set: setSchema, blueprintIds: z.array(z.string()) });
// ---------- Packages and shares ----------
export const createPackageRequest = z.object({
    blueprintId: z.string(),
    version: z.number().int().positive(),
    title: z.string().min(1).max(200),
    intendedUse: z.string().min(1).max(2000),
    contact: z.string().min(1).max(200),
    sampleRunIds: z.array(z.string()).max(MAX_PACKAGE_SAMPLES).default([]),
});
export const recipientSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('USER'), sub: z.string(), email: z.string() }),
    z.object({ type: z.literal('GROUP'), group: roleSchema }),
]);
export const shareSchema = z.object({
    packageId: z.string(),
    recipient: recipientSchema,
    recipientKey: z.string(),
    grantedBy: z.string(),
    grantedAt: isoDate,
});
export const packageSchema = z.object({
    id: z.string(),
    title: z.string(),
    intendedUse: z.string(),
    contact: z.string(),
    authorSub: z.string(),
    authorEmail: z.string(),
    blueprintId: z.string(),
    blueprintOwnerSub: z.string(),
    blueprintOwnerEmail: z.string(),
    blueprintName: z.string(),
    blueprintArn: z.string(),
    version: z.number().int(),
    bdaVersion: z.number().int(),
    schema: bdaSchemaSchema,
    sampleRunIds: z.array(z.string()),
    status: z.enum(['BUILDING', 'READY', 'FAILED']),
    blueprintArchived: z.boolean(),
    createdAt: isoDate,
});
export const packageSummarySchema = packageSchema.omit({ schema: true });
export const packageDetailResponse = z.object({
    package: packageSchema,
    shares: z.array(shareSchema),
    canManage: z.boolean(),
});
export const packageListResponse = paged(packageSummarySchema);
export const addSharesRequest = z.object({ recipients: z.array(recipientSchema).min(1).max(50) });
export const downloadResponse = z.object({ url: z.url(), expiresAt: isoDate });
// ---------- Me and directory ----------
export const meResponse = z.object({
    sub: z.string(),
    email: z.string(),
    name: z.string(),
    roles: z.array(roleSchema),
});
export const directoryUsersResponse = z.object({
    items: z.array(z.object({ sub: z.string(), email: z.string(), name: z.string() })),
});
export const directoryGroupsResponse = z.object({ items: z.array(roleSchema) });
// ---------- Admin ----------
export const dateRangeQuery = z.object({ from: dateOnly, to: dateOnly });
export const adminSummaryResponse = z.object({
    from: dateOnly,
    to: dateOnly,
    totalUsers: z.number().int(),
    activeUsers: z.number().int(),
    dau: z.number().int(),
    wau: z.number().int(),
    mau: z.number().int(),
    blueprintsCreated: z.number().int(),
    versionsPublished: z.number().int(),
    packagesCreated: z.number().int(),
    runsStarted: z.number().int(),
    runsSucceeded: z.number().int(),
    runsFailed: z.number().int(),
    pages: z.number().int(),
    series: z.array(z.object({ date: dateOnly, activeUsers: z.number().int(), runs: z.number().int() })),
});
export const adminUserRowSchema = z.object({
    sub: z.string(),
    name: z.string(),
    email: z.string(),
    roles: z.array(roleSchema),
    enabled: z.boolean(),
    firstSignInAt: isoDate.nullable(),
    lastSignInAt: isoDate.nullable(),
    signIns: z.number().int(),
    blueprintsOwned: z.number().int(),
    setsOwned: z.number().int().optional(),
    runs: z.number().int(),
    pages: z.number().int(),
});
export const adminUsersResponse = z.object({ items: z.array(adminUserRowSchema) });
export const auditRecordSchema = z.object({
    actorSub: z.string(),
    actorRole: z.union([roleSchema, z.literal('System')]),
    action: z.enum(AUDIT_ACTIONS),
    targetType: z.enum(['BLUEPRINT', 'RUN', 'PACKAGE', 'USER', 'EXPORT', 'SESSION', 'SET']),
    targetId: z.string(),
    blueprintName: z.string().optional(),
    version: versionRefSchema.optional(),
    details: z.record(z.string(), z.unknown()).optional(),
    at: isoDate,
});
export const adminTimelineResponse = paged(auditRecordSchema);
export const adminBlueprintListResponse = paged(blueprintSummarySchema);
// ---------- Errors ----------
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.enum([
            'VALIDATION_FAILED',
            'UNAUTHORIZED',
            'FORBIDDEN',
            'NOT_FOUND',
            'CONFLICT',
            'BDA_REJECTED',
            'THROTTLED',
            'INTERNAL',
        ]),
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
        /** The X-Ray root trace ID of the failed request (also in the `x-trace-id` response header), for troubleshooting. */
        traceId: z.string().optional(),
    }),
});
//# sourceMappingURL=api.js.map